"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseAmountStringToNumber = exports.getRedelegationCompletionDate = exports.getRedelegation = exports.canRedelegate = exports.canDelegate = exports.canUndelegate = exports.getMaxEstimatedBalance = exports.getMaxDelegationAvailable = exports.searchFilter = exports.formatValue = exports.mapDelegationInfo = exports.mapRedelegations = exports.mapUnbondings = exports.mapDelegations = exports.COSMOS_MIN_FEES = exports.COSMOS_MIN_SAFE = exports.COSMOS_MAX_DELEGATIONS = exports.COSMOS_MAX_UNBONDINGS = exports.COSMOS_MAX_REDELEGATIONS = void 0;
const invariant_1 = __importDefault(require("invariant"));
const bignumber_js_1 = require("bignumber.js");
const currencies_1 = require("../../currencies");
exports.COSMOS_MAX_REDELEGATIONS = 7;
exports.COSMOS_MAX_UNBONDINGS = 7;
exports.COSMOS_MAX_DELEGATIONS = 5;
exports.COSMOS_MIN_SAFE = new bignumber_js_1.BigNumber(100000); // 100000 uAtom
exports.COSMOS_MIN_FEES = new bignumber_js_1.BigNumber(6000); // 6000 uAtom
function mapDelegations(delegations, validators, unit) {
    return delegations.map(d => {
        var _a;
        const rank = validators.findIndex(v => v.validatorAddress === d.validatorAddress);
        const validator = (_a = validators[rank]) !== null && _a !== void 0 ? _a : d;
        return Object.assign(Object.assign({}, d), { formattedAmount: (0, currencies_1.formatCurrencyUnit)(unit, d.amount, {
                disableRounding: false,
                alwaysShowSign: false,
                showCode: true,
            }), formattedPendingRewards: (0, currencies_1.formatCurrencyUnit)(unit, d.pendingRewards, {
                disableRounding: false,
                alwaysShowSign: false,
                showCode: true,
            }), rank,
            validator });
    });
}
exports.mapDelegations = mapDelegations;
function mapUnbondings(unbondings, validators, unit) {
    return unbondings
        .sort((a, b) => a.completionDate.valueOf() - b.completionDate.valueOf())
        .map(u => {
        const validator = validators.find(v => v.validatorAddress === u.validatorAddress);
        return Object.assign(Object.assign({}, u), { formattedAmount: (0, currencies_1.formatCurrencyUnit)(unit, u.amount, {
                disableRounding: true,
                alwaysShowSign: false,
                showCode: true,
            }), validator });
    });
}
exports.mapUnbondings = mapUnbondings;
function mapRedelegations(redelegations, validators, unit) {
    return redelegations.map(r => {
        const validatorSrc = validators.find(v => v.validatorAddress === r.validatorSrcAddress);
        const validatorDst = validators.find(v => v.validatorAddress === r.validatorDstAddress);
        return Object.assign(Object.assign({}, r), { formattedAmount: (0, currencies_1.formatCurrencyUnit)(unit, r.amount, {
                disableRounding: true,
                alwaysShowSign: false,
                showCode: true,
            }), validatorSrc,
            validatorDst });
    });
}
exports.mapRedelegations = mapRedelegations;
const mapDelegationInfo = (delegations, validators, unit, transaction) => {
    return delegations.map(d => (Object.assign(Object.assign({}, d), { validator: validators.find(v => v.validatorAddress === d.address), formattedAmount: (0, currencies_1.formatCurrencyUnit)(unit, transaction ? transaction.amount : d.amount, {
            disableRounding: true,
            alwaysShowSign: false,
            showCode: true,
        }) })));
};
exports.mapDelegationInfo = mapDelegationInfo;
const formatValue = (value, unit) => value
    .dividedBy(Math.pow(10, unit.magnitude))
    .integerValue(bignumber_js_1.BigNumber.ROUND_FLOOR)
    .toNumber();
exports.formatValue = formatValue;
const searchFilter = query => ({ validator }) => {
    var _a, _b;
    const terms = `${(_a = validator === null || validator === void 0 ? void 0 : validator.name) !== null && _a !== void 0 ? _a : ""} ${(_b = validator === null || validator === void 0 ? void 0 : validator.validatorAddress) !== null && _b !== void 0 ? _b : ""}`;
    return terms.toLowerCase().includes(query.toLowerCase().trim());
};
exports.searchFilter = searchFilter;
function getMaxDelegationAvailable(account, validatorsLength) {
    const numberOfDelegations = Math.min(exports.COSMOS_MAX_DELEGATIONS, validatorsLength || 1);
    const { spendableBalance } = account;
    return spendableBalance
        .minus(exports.COSMOS_MIN_FEES.multipliedBy(numberOfDelegations))
        .minus(exports.COSMOS_MIN_SAFE);
}
exports.getMaxDelegationAvailable = getMaxDelegationAvailable;
const getMaxEstimatedBalance = (a, estimatedFees) => {
    const { cosmosResources } = a;
    let blockBalance = new bignumber_js_1.BigNumber(0);
    if (cosmosResources) {
        blockBalance = cosmosResources.unbondingBalance.plus(cosmosResources.delegatedBalance);
    }
    const amount = a.balance.minus(estimatedFees).minus(blockBalance);
    // If the fees are greater than the balance we will have a negative amount
    // so we round it to 0
    if (amount.lt(0)) {
        return new bignumber_js_1.BigNumber(0);
    }
    return amount;
};
exports.getMaxEstimatedBalance = getMaxEstimatedBalance;
function canUndelegate(account) {
    const { cosmosResources } = account;
    (0, invariant_1.default)(cosmosResources, "cosmosResources should exist");
    return !!(cosmosResources === null || cosmosResources === void 0 ? void 0 : cosmosResources.unbondings) && cosmosResources.unbondings.length < exports.COSMOS_MAX_UNBONDINGS;
}
exports.canUndelegate = canUndelegate;
function canDelegate(account) {
    const maxSpendableBalance = getMaxDelegationAvailable(account, 1);
    return maxSpendableBalance.gt(0);
}
exports.canDelegate = canDelegate;
function canRedelegate(account, delegation) {
    const { cosmosResources } = account;
    (0, invariant_1.default)(cosmosResources, "cosmosResources should exist");
    return (!!(cosmosResources === null || cosmosResources === void 0 ? void 0 : cosmosResources.redelegations) &&
        cosmosResources.redelegations.length < exports.COSMOS_MAX_REDELEGATIONS &&
        !cosmosResources.redelegations.some(rd => rd.validatorDstAddress === delegation.validatorAddress));
}
exports.canRedelegate = canRedelegate;
function getRedelegation(account, delegation) {
    var _a;
    const { cosmosResources } = account;
    const redelegations = (_a = cosmosResources === null || cosmosResources === void 0 ? void 0 : cosmosResources.redelegations) !== null && _a !== void 0 ? _a : [];
    const currentRedelegation = redelegations.find(r => r.validatorDstAddress === delegation.validatorAddress);
    return currentRedelegation;
}
exports.getRedelegation = getRedelegation;
function getRedelegationCompletionDate(account, delegation) {
    const currentRedelegation = getRedelegation(account, delegation);
    return currentRedelegation ? currentRedelegation.completionDate : null;
}
exports.getRedelegationCompletionDate = getRedelegationCompletionDate;
function parseAmountStringToNumber(amountString, unitCode) {
    return amountString.slice(amountString.lastIndexOf(",") + 1).replace(unitCode, "");
}
exports.parseAmountStringToNumber = parseAmountStringToNumber;
//# sourceMappingURL=logic.js.map